import { defineComponent, ref } from 'vue';
import type { ITinyBadgeProps } from './types';
import Icon from '../icon';
import { tv } from 'tailwind-variants';
import { useElementHover } from '@vueuse/core';

const BadgeClass = tv({
  base: 'min-w-40 min-h-[30px] px-1 max-h-[31px] py-1 bg-gray-100 rounded-xs text-gray-500',
  variants: {
    color: {
      primary: 'bg-primary-500',
      primary400: 'bg-primary-400',
      secondary: 'bg-secondary-300',
      info: '',
      default: 'bg-gray-100',
      transparent: '',
      white: 'bg-white',
      gray: 'bg-gray-100 text-gray-500',
      gray500: 'bg-gray-500 text-gray-100',
      gray400: 'bg-gray-400 text-gray-100',
      gray300: 'bg-gray-300 text-gray-100',
      gray200: 'bg-gray-200 text-gray-100',
      success: 'bg-green-100 text-green-500',
      error: 'bg-red-100 text-red-500',
      warning: 'bg-orange-300',
      'primary-light': 'bg-primary-100',
      errorInfo: 'bg-transparent border-red-500 border rounded-sm text-red-500 hover:bg-red-100',
      blue: 'bg-primary-100 text-primary-500',
      blue800: 'bg-primary-800 text-primary-200',
      blue700: 'bg-primary-700 text-primary-200',
      purple: 'bg-purple-100 text-purple-500',
      teal: 'bg-teal-100 text-teal-500',
    },
    outlined: {
      true: 'bg-transparent border rounded-sm text-red-500 hover:bg-red-100',
    },
    size: {
      xxs: 'min-w-4 w-17 px-2 text-[12px] leading-[18px] font-semibold py-0.5',
      xs: 'min-w-4 px-2 text-xs leading-[18px] font-semibold py-0.5',
      s: 'min-w-32',
      m: 'min-w-24',
      lg: 'l',
    },
    type: {
      mltslct: 'min-h-[22px] min-w-[84px] py-0.5 px-2.5',
    },
    extraRound: {
      true: 'rounded-lg',
    },
  },
  compoundVariants: [
    {
      outlined: true,
      color: 'blue',
      className: 'bg-transparent border border-primary-500 text-primary-500 hover:bg-primary-100',
    },
  ],
});

const TinyBadge = defineComponent<ITinyBadgeProps>({
  name: 'TinyBadge',
  setup(props, ctx) {
    const elref = ref();
    const hover = useElementHover(elref);
    const computedSize = hover.value ? 'm' : props.size ?? 'm'
    return () => {
      return (
        <div
          ref={elref}
          class={BadgeClass({
            color: props.variant as any,
            size: computedSize,
            outlined: !!props.outlined,
            extraRound: !!props.extraRound,
            type: props.type as any,
          })}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: props.icon ? 'max-content max-content' : 'auto',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
            }}
          >
            {props.icon && (
              <div
                class="text-gray-500 "
                style={{
                  display: 'grid',
                  justifyContent: 'start',
                }}
              >
                <Icon fill="currentColor" src={props.icon} />
              </div>
            )}
            <div class={`font-medium flex justify-center align-center whitespace-nowrap cursor-default ${props.textClass || ''}`}>
              {hover.value && props.hoverText
                ? props.hoverText
                : ctx.slots.default
                  ? ctx.slots.default()
                  : ''}
            </div>
          </div>
        </div>
      );
    };
  },
});

TinyBadge.props = ['icon', 'variant', 'size', 'hoverText', 'outlined', 'extraRound', 'type', 'textClass'];

export default TinyBadge;
